import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { Breadcrumbs, type BreadcrumbType } from "ui/breadcrumbs";
import { Chip } from "ui/chip";
import { Box } from "ui/box";
import { Button } from "ui/button";
import { InfoMessage } from "ui/info-message";
import { Tooltip } from "ui/tooltip";
import { Typography } from "ui/typography";
import { type SidebarMenuItem } from "components/partials/sidebar-menu";
import FormBuilder, { type DynamicFormSchema } from "containers/admin/clients/client/components/form-builder/form-builder";
import type { FormBuilderActions } from "containers/admin/clients/client/components/form-builder/form-builder-action-button";
import FormBuilderViewer from "containers/admin/clients/client/components/form-builder/form-builder-viewer";
import { getSectionInfoForReadOnly } from "containers/admin/clients/client/components/form-builder/form-builder-viewer-utils";
import {
  getSectionActions,
  getSectionInfo,
  getSectionLayout,
  getSectionNeedsGeneration,
  isEmptyFormValue,
  type FormSchemaSidebarItem,
} from "containers/admin/clients/client/components/form-builder/form-schema";
import { SchemaSidebar } from "containers/admin/clients/client/components/schema-sidebar/schema-sidebar";

import Icons from "assets";

import { useOptionalCurrentClient } from "hooks/api/use-clients";
import { useUnsavedChangesGuard } from "hooks/use-unsaved-changes-guard";

import { getClientPaths } from "utilities/app-routes";
import { formatFiscalYear, getCurrentFiscalStartYear } from "utilities";

type ClientPaths = ReturnType<typeof getClientPaths>;

export interface FormPageTab {
  id: string;
  label: string;
  schema: DynamicFormSchema;
  dataPath?: string[];
}

export type FormPageBreadcrumbParent = {
  label: string;
  pathKey?: keyof ClientPaths;
  active?: boolean;
};

export interface FormPageBreadcrumbConfig {
  label: string;
  pathKey?: keyof ClientPaths;
  parents?: FormPageBreadcrumbParent[];
  includeActiveTab?: boolean;
}

export interface FormPageHeaderConfig {
  title: string;
  showFiscalYearBadge?: boolean;
}

export interface FormPageLayoutProps {
  formId: string;
  sidebarSectionId: string;
  tabs: FormPageTab[];
  sidebarItems?: FormSchemaSidebarItem[];
  breadcrumb?: FormPageBreadcrumbConfig;
  customBreadcrumbs?: BreadcrumbType[];
  header?: FormPageHeaderConfig;
  renderPageHeader?: (editActions: React.ReactNode) => React.ReactNode;
  rootClassName?: string;
  getTabValues?: (tab: FormPageTab) => Record<string, unknown>;
  onTabValuesChange?: (tab: FormPageTab, values: Record<string, unknown>) => void;
  tabRevisions?: Record<string, number>;
  isSaveDisabled?: boolean;
  isDiscardDisabled?: boolean;
  isSaving?: boolean;
  isEditing?: boolean;
  onIsEditingChange?: (isEditing: boolean) => void;
  editButtonLabel?: string;
  onSubmit?: (data: Record<string, unknown>, tab: FormPageTab) => void;
  onDiscard?: () => void;
  formActions?: FormBuilderActions;
}

const DEFAULT_ROOT_CLASS_NAME =
  "flex flex-col flex-1 overflow-hidden bg-gray-100 pb-5 pl-3 pr-5 pt-3 gap-3";

const SECTION_NEEDS_GENERATION_MESSAGE = "Some required information missing";

const SECTION_NEEDS_GENERATION_ICON = (
  <Tooltip
    label={SECTION_NEEDS_GENERATION_MESSAGE}
    placement="bottom"
    wrapperSx={{ display: "inline-flex", alignItems: "center" }}>
    <Box
      component="span"
      sx={(theme) => ({ display: "inline-flex", alignItems: "center", color: theme.palette.grey[500] })}>
      <Icons.InfoIndicator width={20} height={20} />
    </Box>
  </Tooltip>
);

const getTabNeedsGeneration = (
  tabId: string,
  tabs: FormPageTab[],
  getTabValues?: (tab: FormPageTab) => Record<string, unknown>,
): boolean => {
  if (!getTabValues) {
    return false;
  }

  const tab = tabs.find((candidate) => candidate.id === tabId);

  return tab ? getSectionNeedsGeneration(tab.schema, getTabValues(tab)) : false;
};

const getGenerationIconProps = (
  tabId: string,
  tabs: FormPageTab[],
  getTabValues?: (tab: FormPageTab) => Record<string, unknown>,
): { endIcon: React.ReactNode } | Record<string, never> =>
  getTabNeedsGeneration(tabId, tabs, getTabValues) ? { endIcon: SECTION_NEEDS_GENERATION_ICON } : {};

// Only leaf sections (a tab's own sidebar row, or a group's children) carry the icon — a group
// header like "Creatives" never shows it, even when a child underneath does.
const mapSidebarItems = (
  items: FormSchemaSidebarItem[],
  tabs: FormPageTab[],
  onSelect: (tabId: string) => void,
  getTabValues?: (tab: FormPageTab) => Record<string, unknown>,
): SidebarMenuItem[] =>
  items.map((item) => {
    if (item.children) {
      return {
        id: item.id,
        label: item.label,
        children: item.children.map((child) => ({
          id: child.id,
          label: child.label,
          onSelect: () => onSelect(child.id),
          ...getGenerationIconProps(child.id, tabs, getTabValues),
        })),
      };
    }

    return {
      id: item.id,
      label: item.label,
      onSelect: () => onSelect(item.id),
      ...getGenerationIconProps(item.id, tabs, getTabValues),
    };
  });

const FormPageLayout: React.FC<FormPageLayoutProps> = ({
  formId,
  sidebarSectionId,
  tabs,
  sidebarItems,
  breadcrumb,
  customBreadcrumbs,
  header,
  renderPageHeader,
  rootClassName = DEFAULT_ROOT_CLASS_NAME,
  getTabValues,
  onTabValuesChange,
  tabRevisions,
  isSaveDisabled = true,
  isDiscardDisabled = true,
  isSaving = false,
  isEditing = false,
  onIsEditingChange,
  editButtonLabel = "Edit",
  onSubmit,
  onDiscard,
  formActions,
}) => {
  const { clientId } = useParams<{ clientId: string }>();
  const client = useOptionalCurrentClient();
  const [activeTabId, setActiveTabId] = useState(() => tabs[0]?.id ?? "");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const paths = getClientPaths({ clientId });
  const isEditable = Boolean(getTabValues && onIsEditingChange);
  const isDirty = !isDiscardDisabled;

  // Only the isEditable + isEditing Cancel flow below uses this guard - the isEditable=false
  // "Discard changes" button below discards immediately, with no confirmation.
  const { handleCancelClick, unsavedChangesDialog } = useUnsavedChangesGuard({
    isEditing,
    isDirty: isEditable && isDirty,
    onDiscard: () => {
      onDiscard?.();
      onIsEditingChange?.(false);
    },
  });

  useEffect(() => {
    if (!tabs.length) {
      return;
    }

    if (!tabs.some((tab) => tab.id === activeTabId)) {
      setActiveTabId(tabs[0].id);
    }
  }, [activeTabId, tabs]);

  const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? tabs[0];
  const activeTabValues = activeTab && getTabValues ? getTabValues(activeTab) : undefined;

  const breadcrumbs = useMemo(() => {
    if (customBreadcrumbs) {
      return customBreadcrumbs;
    }

    if (!breadcrumb) {
      return [];
    }

    if (!breadcrumb || !client) {
      return [];
    }

    const items: BreadcrumbType[] = [
      { label: client.name, href: paths.clientCampaignsPath },
      ...(breadcrumb.parents?.map((parent) => ({
        label: parent.label,
        ...(parent.active === false ? { active: false as const } : {}),
        ...(parent.pathKey ? { href: paths[parent.pathKey] } : {}),
      })) ?? []),
      {
        label: breadcrumb.label,
        ...(breadcrumb.pathKey ? { href: paths[breadcrumb.pathKey] } : {}),
      },
    ];

    if (breadcrumb.includeActiveTab !== false && activeTab) {
      items.push({ label: activeTab.label });
    }

    return items;
  }, [activeTab?.label, breadcrumb, client?.name, customBreadcrumbs, paths]);

  const sections = useMemo(
    () => [
      {
        id: sidebarSectionId,
        items: sidebarItems
          ? mapSidebarItems(sidebarItems, tabs, setActiveTabId, getTabValues)
          : tabs.map((tab) => ({
              id: tab.id,
              label: tab.label,
              onSelect: () => setActiveTabId(tab.id),
              ...getGenerationIconProps(tab.id, tabs, getTabValues),
            })),
      },
    ],
    [sidebarSectionId, sidebarItems, tabs, getTabValues],
  );

  const handleFormSubmit = (data: Record<string, unknown>) => {
    if (!activeTab) {
      return;
    }

    onSubmit?.(data, activeTab);
  };

  const handleDiscard = () => {
    onDiscard?.();
  };

  const handleEnterEditMode = () => {
    onIsEditingChange?.(true);
  };

  const handleActiveTabValuesChange = (values: Record<string, unknown>) => {
    if (!activeTab) {
      return;
    }

    onTabValuesChange?.(activeTab, values);
  };

  const renderEditActions = () => {
    if (!isEditable) {
      return (
        <>
          <Button variant="contained" onClick={handleDiscard} disabled={isDiscardDisabled || isSaving}>
            Discard changes
          </Button>
          <Button type="submit" form={formId} disabled={isSaveDisabled || isSaving}>
            Save
          </Button>
        </>
      );
    }

    if (isEditing) {
      return (
        <>
          <Button
            variant="text"
            size="large"
            onClick={handleCancelClick}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="large"
            type="submit"
            form={formId}
            loading={isSaving}
            disabled={isSaveDisabled || isSaving}
          >
            Save changes
          </Button>
        </>
      );
    }

    return (
      <Button variant="contained" size="large" onClick={handleEnterEditMode}>
        {editButtonLabel}
      </Button>
    );
  };

  const renderTabContent = () => {
    if (!activeTab) {
      return null;
    }

    if (getTabValues) {
      const tabValues = activeTabValues ?? {};
      const isCompactSectionLayout = getSectionLayout(activeTab.schema) === "compact";
      const showViewer = isEditable && !isEditing;
      const activeTabSectionInfo = showViewer
        ? getSectionInfoForReadOnly(activeTab.schema)
        : getSectionInfo(activeTab.schema);

      const tabBody = showViewer ? (
        <FormBuilderViewer schema={activeTab.schema} values={tabValues} actions={formActions} />
      ) : (
        <FormBuilder
          key={`${activeTab.id}-${tabRevisions?.[activeTab.id] ?? 0}`}
          formId={formId}
          schema={activeTab.schema}
          defaultValues={tabValues}
          onValuesChange={handleActiveTabValuesChange}
          onSubmit={handleFormSubmit}
          actions={formActions}
        />
      );

      if (isCompactSectionLayout) {
        return (
          <div className={showViewer ? "w-full flex flex-col gap-1.5" : "max-w-[600px] flex flex-col gap-1.5"}>
            {activeTabSectionInfo && <InfoMessage color="info">{activeTabSectionInfo}</InfoMessage>}
            {tabBody}
          </div>
        );
      }

      return (
        <div className="flex flex-col gap-3">
          {activeTabSectionInfo && <InfoMessage color="info">{activeTabSectionInfo}</InfoMessage>}
          <div className={showViewer ? "w-full" : "max-w-[600px]"}>{tabBody}</div>
        </div>
      );
    }

    return (
      <FormBuilder
        key={activeTabId}
        formId={formId}
        schema={activeTab.schema}
        onSubmit={handleFormSubmit}
        actions={formActions}
      />
    );
  };

  const fiscalYearLabel = formatFiscalYear(getCurrentFiscalStartYear());
  const editActions = renderEditActions();

  if (!activeTab) {
    return null;
  }

  const sectionActions = getSectionActions(activeTab.schema).flatMap((action) => {
    const onAction = formActions?.[action.id];

    if (!onAction) {
      return [];
    }

    if (
      action.hideWhenFieldEmpty &&
      isEmptyFormValue(activeTabValues?.[action.hideWhenFieldEmpty])
    ) {
      return [];
    }

    return [
      <Button
        key={action.id}
        type="button"
        color={action.color}
        variant={action.variant ?? "outlined"}
        size={action.size}
        onClick={onAction}
        data-testid={`form-section-action-${action.id}`}
      >
        {action.label}
      </Button>,
    ];
  });

  const tabTitle = (
    <div className="flex max-w-[600px] items-center justify-between gap-3">
      <Typography component="h3" variant="h4" sx={(theme) => ({ color: theme.palette.secondary.main })}>
        {activeTab.label}
      </Typography>
      <div className="flex items-center gap-3">{sectionActions}</div>
    </div>
  );

  return (
    <div className={rootClassName}>
      {unsavedChangesDialog}
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      {renderPageHeader?.(editActions)}
      {!renderPageHeader && header && (
        <div className="flex shrink-0 items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold leading-8 text-navy-800">{header.title}</h1>
            {header.showFiscalYearBadge && (
              <Chip size="small" variant="outlined" label={fiscalYearLabel} />
            )}
          </div>
          <div className="flex items-center gap-3">{editActions}</div>
        </div>
      )}
      <div className="flex flex-1 overflow-hidden bg-white border border-gray-200 rounded-lg">
        {tabs.length > 1 && (
          <SchemaSidebar
            isCollapsed={isSidebarCollapsed}
            onCollapsedChange={setIsSidebarCollapsed}
            sections={sections}
            activeItemId={activeTabId}
            onItemSelect={(item) => setActiveTabId(item.id)}
          />
        )}
        <article className="flex-1 overflow-y-auto flex flex-col bg-white p-4 gap-2">
          {!header && !renderPageHeader && (
            <div className="flex items-center">
              {tabTitle}
              <div className="flex gap-3 ml-auto">{editActions}</div>
            </div>
          )}
          {(header || renderPageHeader) && tabTitle}
          <div>{renderTabContent()}</div>
        </article>
      </div>
    </div>
  );
};

export default FormPageLayout;
